import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Box,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Button,
  Chip,
  Alert,
  Card,
  CardContent,
  Stack,
  Divider,
  LinearProgress,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Refresh from "@mui/icons-material/Refresh";
import Storage from "@mui/icons-material/Storage";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Error from "@mui/icons-material/Error";
import Warning from "@mui/icons-material/Warning";
import NetworkCheck from "@mui/icons-material/NetworkCheck";
import Dns from "@mui/icons-material/Dns";
import ConfirmationNumber from "@mui/icons-material/ConfirmationNumber";
import Schedule from "@mui/icons-material/Schedule";
import Business from "@mui/icons-material/Business";

// API URLs
const BRANCH_URL = `${process.env.REACT_APP_API_URL}/api/dpt/branch`;

// Network status colors
const NETWORK_COLORS = {
  active: "#00e676",
  down: "#ff4444",
  intermittent: "#ff9800",
  serverDisconnected: "#9e9e9e",
};

const NTKDashboardSimple = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const [lastUpdateData, setLastUpdateData] = useState({
    lastUpdate: null,
    shiftType: null,
    serverStatus: null,
    systemStatus: null,
  });

  const safeExtractArray = (response, defaultValue = []) => {
    if (!response) return defaultValue;
    if (response.data?.data && Array.isArray(response.data.data)) return response.data.data;
    if (response.data && Array.isArray(response.data)) return response.data;
    if (Array.isArray(response)) return response;
    return defaultValue;
  };

  const getNetworkStatus = (branch) => {
    if (!branch) return "unknown";
    const status = (branch.status || "").toLowerCase();
    if (status.includes("down")) return "down";
    if (status.includes("int") || status.includes("intermittent")) return "intermittent";
    if (status.includes("active") || status.includes("up") || status === "connected") return "active";
    if (status.includes("server disconnected")) return "server disconnected";
    return "unknown";
  };

  const fetchBranchData = useCallback(async () => {
    try {
      setRefreshing(true);
      setError(null);

      const token = localStorage.getItem("token");
      if (!token) {
        setError("No authentication token found. Please login again.");
        setLoading(false);
        setRefreshing(false);
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };

      const [branchesRes, ticketsRes, lastUpdateRes] = await Promise.all([
        axios.get(`${BRANCH_URL}/all`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${BRANCH_URL}/tickets`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${BRANCH_URL}/lastUpdate`, { headers }).catch(() => ({ data: {} })),
      ]);

      const branches = safeExtractArray(branchesRes);
      const tickets = safeExtractArray(ticketsRes);

      const updateData = {
        lastUpdate: lastUpdateRes.data?.lastUpdate || "—",
        shiftType: lastUpdateRes.data?.shiftType || "Normal",
        serverStatus: lastUpdateRes.data?.serverStatus || "Unknown",
        systemStatus: lastUpdateRes.data?.systemStatus || "Active",
      };
      setLastUpdateData(updateData);

      // Calculate branch statistics
      const totalBranches = branches.length;
      let activeCount = 0, downCount = 0, intermittentCount = 0, serverDisconnectedCount = 0;

      branches.forEach((branch) => {
        const status = getNetworkStatus(branch);
        if (status === "active") activeCount++;
        else if (status === "down") downCount++;
        else if (status === "intermittent") intermittentCount++;
        else if (status === "server disconnected") serverDisconnectedCount++;
      });

      // Calculate ticket statistics
      let openTickets = 0;
      let closedTickets = 0;
      let totalTicketTimeLoss = 0;
      let closedTicketsCount = 0;

      tickets.forEach((ticket) => {
        const ticketStatus = (ticket.ticketStatus || "").toLowerCase();
        if (ticketStatus === "open") {
          openTickets++;
        } else if (ticketStatus === "closed") {
          closedTickets++;
          if (ticket.openedAt && ticket.closedAt) {
            const opened = new Date(ticket.openedAt);
            const closed = new Date(ticket.closedAt);
            const diffMs = closed - opened;
            const timeLoss = Math.floor(diffMs / (1000 * 60));
            totalTicketTimeLoss += timeLoss;
            closedTicketsCount++;
          }
        }
      });

      const avgTicketTimeLoss = closedTicketsCount > 0
        ? Math.round(totalTicketTimeLoss / closedTicketsCount)
        : 0;

      const ticketResolutionRate = tickets.length > 0
        ? ((closedTickets / tickets.length) * 100).toFixed(1)
        : 0;

      // Calculate region breakdown
      const regionStats = {};
      branches.forEach((branch) => {
        const region = branch.region || "Unknown";
        const status = getNetworkStatus(branch);
        if (!regionStats[region]) {
          regionStats[region] = { total: 0, active: 0, down: 0, intermittent: 0 };
        }
        regionStats[region].total++;
        if (status === "active") regionStats[region].active++;
        else if (status === "down") regionStats[region].down++;
        else if (status === "intermittent") regionStats[region].intermittent++;
      });

      const regionBreakdown = Object.entries(regionStats)
        .map(([name, stats]) => ({
          name,
          ...stats,
          activePercentage: stats.total > 0 ? ((stats.active / stats.total) * 100).toFixed(1) : 0,
          downPercentage: stats.total > 0 ? ((stats.down / stats.total) * 100).toFixed(1) : 0,
          intermittentPercentage: stats.total > 0 ? ((stats.intermittent / stats.total) * 100).toFixed(1) : 0,
        }))
        .sort((a, b) => b.total - a.total);

      const systemStatus = updateData.systemStatus === "Active" ? {
        text: "Excellent",
        color: NETWORK_COLORS.active,
        icon: "✅",
        description: "System is operating normally",
      } : {
        text: "Critical",
        color: NETWORK_COLORS.down,
        icon: "🔴",
        description: "System is down",
      };

      setData({
        summary: {
          totalBranches,
          active: activeCount,
          down: downCount,
          intermittent: intermittentCount,
          serverDisconnected: serverDisconnectedCount,
          activePercentage: totalBranches > 0 ? ((activeCount / totalBranches) * 100).toFixed(1) : 0,
          downPercentage: totalBranches > 0 ? ((downCount / totalBranches) * 100).toFixed(1) : 0,
          intermittentPercentage: totalBranches > 0 ? ((intermittentCount / totalBranches) * 100).toFixed(1) : 0,
        },
        tickets: {
          total: tickets.length,
          open: openTickets,
          closed: closedTickets,
          openPercentage: tickets.length > 0 ? ((openTickets / tickets.length) * 100).toFixed(1) : 0,
          closedPercentage: parseFloat(ticketResolutionRate),
          totalTimeLoss: totalTicketTimeLoss,
          avgTimeLoss: avgTicketTimeLoss,
        },
        regionBreakdown,
        systemStatus,
        lastUpdated: new Date().toLocaleString(),
      });
    } catch (err) {
      console.error("Fetch error:", err);
      setError(err.message || "Failed to fetch data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchBranchData();
  }, [fetchBranchData]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" sx={{ bgcolor: "#FFFFFF" }}>
        <Box textAlign="center">
          <CircularProgress sx={{ color: "#FFD600", mb: 2 }} size={isMobile ? 44 : 60} />
          <Typography variant="h6" sx={{ color: "#000000" }}>Loading Dashboard...</Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={{ xs: 2, sm: 3 }} sx={{ bgcolor: "#FFFFFF", minHeight: "100vh" }}>
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={fetchBranchData}>Retry</Button>}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!data) return null;

  // Determine card layout
  const getCardWidth = () => {
    if (isMobile) return "100%";
    if (isTablet) return "calc(50% - 12px)";
    return "calc(25% - 18px)";
  };

  const getRegionWidth = () => {
    if (isMobile) return "100%";
    if (isTablet) return "calc(50% - 12px)";
    return "calc(33.33% - 16px)";
  };

  const cardWidth = getCardWidth();
  const regionWidth = getRegionWidth();

  return (
    <Box sx={{ bgcolor: "#FFFFFF", minHeight: "100vh", py: { xs: 1, sm: 2 } }}>
      <Container
        maxWidth={false}
        disableGutters
        sx={{
          width: { xs: "100%", md: "calc(100% - 96px)" },
          ml: { xs: 0, md: "84px" },
          mr: { xs: 0, md: "12px" },
          px: { xs: 1.5, sm: 2, md: 0.5 },
          boxSizing: "border-box",
        }}
      >
        {/* Header */}
        <Paper
          sx={{
            p: { xs: 2, sm: 3 },
            mb: { xs: 2, sm: 4 },
            background: "linear-gradient(135deg, #FFD600 0%, #FFC107 100%)",
            borderBottom: "4px solid #000000",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            flexDirection={{ xs: "column", sm: "row" }}
            flexWrap="wrap"
            gap={2}
          >
            <Box>
              <Typography
                variant={isMobile ? "h5" : "h4"}
                fontWeight="bold"
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <NetworkCheck sx={{ fontSize: { xs: 28, sm: 40 }, color: "#000000" }} />
                <span style={{ color: "#000000" }}>Network Dashboard</span>
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: "#333333" }}>
                Real-time branch and ticket monitoring
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={fetchBranchData}
              disabled={refreshing}
              fullWidth={isMobile}
              sx={{
                bgcolor: "#000000",
                color: "#FFD600",
                fontWeight: "bold",
                "&:hover": { bgcolor: "#333333" },
                px: 4,
              }}
            >
              {refreshing ? "Refreshing..." : "Refresh"}
            </Button>
          </Box>
        </Paper>

        {/* 7 Cards - Branch and Ticket Info */}
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 }, backgroundColor: "#FFFFFF", border: "1px solid #FFD600" }}>
          <Typography variant="h6" fontWeight="bold" mb={2} sx={{ color: "#000000", display: "flex", alignItems: "center", gap: 1 }}>
            <Storage sx={{ color: "#FFD600" }} /> Branch Status
          </Typography>
          
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              justifyContent: isMobile ? "center" : "flex-start",
              mb: 4,
            }}
          >
            {/* Card 1: Total Branches */}
            <Box sx={{ width: cardWidth, flexShrink: 0, flexGrow: 0 }}>
              <Card sx={{ 
                height: "100%", 
                minHeight: 200,
                borderTop: `4px solid #FFD600`, 
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                display: "flex",
                flexDirection: "column",
              }}>
                <CardContent sx={{ 
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  p: 2.5,
                }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                    <Storage sx={{ fontSize: 35, color: "#FFD600" }} />
                    <Chip label="Total" size="small" sx={{ bgcolor: "#FFD60020", fontWeight: "bold" }} />
                  </Box>
                  <Typography variant="h3" fontWeight="bold" sx={{ color: "#000000", mb: 0.5, fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" } }}>
                    {data.summary.totalBranches}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Total Branches
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: "auto" }}>
                    <Chip size="small" icon={<CheckCircle sx={{ fontSize: 12 }} />} label={`${data.summary.active}`} sx={{ bgcolor: `${NETWORK_COLORS.active}20`, color: NETWORK_COLORS.active, fontWeight: "bold" }} />
                    <Chip size="small" icon={<Error sx={{ fontSize: 12 }} />} label={`${data.summary.down}`} sx={{ bgcolor: `${NETWORK_COLORS.down}20`, color: NETWORK_COLORS.down, fontWeight: "bold" }} />
                    <Chip size="small" icon={<Warning sx={{ fontSize: 12 }} />} label={`${data.summary.intermittent}`} sx={{ bgcolor: `${NETWORK_COLORS.intermittent}20`, color: NETWORK_COLORS.intermittent, fontWeight: "bold" }} />
                  </Stack>
                </CardContent>
              </Card>
            </Box>

            {/* Card 2: Active Branches */}
            <Box sx={{ width: cardWidth, flexShrink: 0, flexGrow: 0 }}>
              <Card sx={{ 
                height: "100%", 
                minHeight: 200,
                borderTop: `4px solid ${NETWORK_COLORS.active}`, 
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                display: "flex",
                flexDirection: "column",
              }}>
                <CardContent sx={{ 
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  p: 2.5,
                }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                    <CheckCircle sx={{ fontSize: 35, color: NETWORK_COLORS.active }} />
                    <Chip label="Active" size="small" sx={{ bgcolor: `${NETWORK_COLORS.active}20`, fontWeight: "bold" }} />
                  </Box>
                  <Typography variant="h3" fontWeight="bold" sx={{ color: NETWORK_COLORS.active, mb: 0.5, fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" } }}>
                    {data.summary.active}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Branches Online
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ mt: "auto" }}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2" color="textSecondary">Health Rate</Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: NETWORK_COLORS.active }}>{data.summary.activePercentage}%</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={parseFloat(data.summary.activePercentage)} sx={{ height: 6, borderRadius: 3, bgcolor: "#e0e0e0", "& .MuiLinearProgress-bar": { bgcolor: NETWORK_COLORS.active } }} />
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Card 3: Down & Intermittent Combined */}
            <Box sx={{ width: cardWidth, flexShrink: 0, flexGrow: 0 }}>
              <Card sx={{ 
                height: "100%", 
                minHeight: 200,
                borderTop: `4px solid ${NETWORK_COLORS.down}`, 
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                display: "flex",
                flexDirection: "column",
              }}>
                <CardContent sx={{ 
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  p: 2.5,
                }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <Error sx={{ fontSize: 30, color: NETWORK_COLORS.down }} />
                      <Warning sx={{ fontSize: 30, color: NETWORK_COLORS.intermittent }} />
                    </Box>
                    <Chip label="Issues" size="small" sx={{ bgcolor: `${NETWORK_COLORS.down}20`, fontWeight: "bold" }} />
                  </Box>
                  
                  <Box display="flex" alignItems="baseline" gap={1} mb={0.5}>
                    <Typography variant="h3" fontWeight="bold" sx={{ color: NETWORK_COLORS.down, fontSize: { xs: "1.8rem", sm: "2.2rem", md: "2.8rem" } }}>
                      {data.summary.down}
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" sx={{ color: NETWORK_COLORS.intermittent, fontSize: { xs: "1.5rem", sm: "1.8rem", md: "2.2rem" } }}>
                      / {data.summary.intermittent}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Down / Intermittent Branches
                  </Typography>
                  
                  <Divider sx={{ my: 1 }} />
                  
                  <Box sx={{ mt: "auto" }}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2" color="textSecondary">Down Rate</Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: NETWORK_COLORS.down }}>{data.summary.downPercentage}%</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={parseFloat(data.summary.downPercentage)} sx={{ height: 5, borderRadius: 3, bgcolor: "#e0e0e0", "& .MuiLinearProgress-bar": { bgcolor: NETWORK_COLORS.down } }} />
                    
                    <Box display="flex" justifyContent="space-between" mt={1} mb={0.5}>
                      <Typography variant="body2" color="textSecondary">Intermittent Rate</Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: NETWORK_COLORS.intermittent }}>{data.summary.intermittentPercentage}%</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={parseFloat(data.summary.intermittentPercentage)} sx={{ height: 5, borderRadius: 3, bgcolor: "#e0e0e0", "& .MuiLinearProgress-bar": { bgcolor: NETWORK_COLORS.intermittent } }} />
                    
                    <Box display="flex" justifyContent="space-between" mt={1}>
                      <Typography variant="body2" color="textSecondary">Total Issues</Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: "#ff4444" }}>
                        {data.summary.down + data.summary.intermittent}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Card 4: System Status */}
            <Box sx={{ width: cardWidth, flexShrink: 0, flexGrow: 0 }}>
              <Card sx={{ 
                height: "100%", 
                minHeight: 200,
                borderTop: `4px solid ${data.systemStatus.color}`, 
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                display: "flex",
                flexDirection: "column",
              }}>
                <CardContent sx={{ 
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  p: 2.5,
                }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                    <Dns sx={{ fontSize: 35, color: data.systemStatus.color }} />
                    <Chip label="Status" size="small" sx={{ bgcolor: `${data.systemStatus.color}20`, fontWeight: "bold" }} />
                  </Box>
                  <Box display="flex" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
                    <Typography variant="h2" sx={{ fontSize: { xs: 28, sm: 35, md: 40 } }}>{data.systemStatus.icon}</Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: data.systemStatus.color, fontSize: { xs: "1.1rem", sm: "1.25rem", md: "1.5rem" } }}>
                      {data.systemStatus.text}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    {data.systemStatus.description}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ mt: "auto" }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="textSecondary">Server:</Typography>
                      <Chip label={lastUpdateData.serverStatus} size="small" sx={{ bgcolor: lastUpdateData.serverStatus === "Online" ? "#00e67620" : "#ff444420", color: lastUpdateData.serverStatus === "Online" ? "#00e676" : "#ff4444", fontWeight: "bold" }} />
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.5}>
                      <Typography variant="body2" color="textSecondary">Shift:</Typography>
                      <Typography variant="body2" fontWeight="bold">{lastUpdateData.shiftType}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>

          {/* Ticket Section */}
          <Typography variant="h6" fontWeight="bold" mb={2} sx={{ color: "#000000", display: "flex", alignItems: "center", gap: 1 }}>
            <ConfirmationNumber sx={{ color: "#FFD600" }} /> Ticket Status
          </Typography>
          
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              justifyContent: isMobile ? "center" : "flex-start",
            }}
          >
            {/* Card 5: Total Tickets */}
            <Box sx={{ width: cardWidth, flexShrink: 0, flexGrow: 0 }}>
              <Card sx={{ 
                height: "100%", 
                minHeight: 200,
                borderTop: `4px solid #9c27b0`, 
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                display: "flex",
                flexDirection: "column",
              }}>
                <CardContent sx={{ 
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  p: 2.5,
                }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                    <ConfirmationNumber sx={{ fontSize: 35, color: "#9c27b0" }} />
                    <Chip label="Total" size="small" sx={{ bgcolor: "#9c27b020", fontWeight: "bold" }} />
                  </Box>
                  <Typography variant="h3" fontWeight="bold" sx={{ color: "#9c27b0", mb: 0.5, fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" } }}>
                    {data.tickets.total}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Total Tickets
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: "auto" }}>
                    <Chip size="small" label={`Open: ${data.tickets.open}`} sx={{ bgcolor: "#ff980020", color: "#ff9800", fontWeight: "bold" }} />
                    <Chip size="small" label={`Closed: ${data.tickets.closed}`} sx={{ bgcolor: "#00e67620", color: "#00e676", fontWeight: "bold" }} />
                  </Stack>
                </CardContent>
              </Card>
            </Box>

            {/* Card 6: Open Tickets */}
            <Box sx={{ width: cardWidth, flexShrink: 0, flexGrow: 0 }}>
              <Card sx={{ 
                height: "100%", 
                minHeight: 200,
                borderTop: `4px solid #ff9800`, 
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                display: "flex",
                flexDirection: "column",
              }}>
                <CardContent sx={{ 
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  p: 2.5,
                }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                    <Warning sx={{ fontSize: 35, color: "#ff9800" }} />
                    <Chip label="Open" size="small" sx={{ bgcolor: "#ff980020", fontWeight: "bold" }} />
                  </Box>
                  <Typography variant="h3" fontWeight="bold" sx={{ color: "#ff9800", mb: 0.5, fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" } }}>
                    {data.tickets.open}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Open Tickets
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ mt: "auto" }}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2" color="textSecondary">Open Rate</Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: "#ff9800" }}>{data.tickets.openPercentage}%</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={parseFloat(data.tickets.openPercentage)} sx={{ height: 6, borderRadius: 3, bgcolor: "#e0e0e0", "& .MuiLinearProgress-bar": { bgcolor: "#ff9800" } }} />
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Card 7: Closed Tickets */}
            <Box sx={{ width: cardWidth, flexShrink: 0, flexGrow: 0 }}>
              <Card sx={{ 
                height: "100%", 
                minHeight: 200,
                borderTop: `4px solid #00e676`, 
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                display: "flex",
                flexDirection: "column",
              }}>
                <CardContent sx={{ 
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  p: 2.5,
                }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                    <CheckCircle sx={{ fontSize: 35, color: "#00e676" }} />
                    <Chip label="Closed" size="small" sx={{ bgcolor: "#00e67620", fontWeight: "bold" }} />
                  </Box>
                  <Typography variant="h3" fontWeight="bold" sx={{ color: "#00e676", mb: 0.5, fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" } }}>
                    {data.tickets.closed}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Closed Tickets
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ mt: "auto" }}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2" color="textSecondary">Resolution Rate</Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: "#00e676" }}>{data.tickets.closedPercentage}%</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={parseFloat(data.tickets.closedPercentage)} sx={{ height: 6, borderRadius: 3, bgcolor: "#e0e0e0", "& .MuiLinearProgress-bar": { bgcolor: "#00e676" } }} />
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Card 8: Avg Resolution Time */}
            <Box sx={{ width: cardWidth, flexShrink: 0, flexGrow: 0 }}>
              <Card sx={{ 
                height: "100%", 
                minHeight: 200,
                borderTop: `4px solid #2196f3`, 
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                display: "flex",
                flexDirection: "column",
              }}>
                <CardContent sx={{ 
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  p: 2.5,
                }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                    <Schedule sx={{ fontSize: 35, color: "#2196f3" }} />
                    <Chip label="Avg Time" size="small" sx={{ bgcolor: "#2196f320", fontWeight: "bold" }} />
                  </Box>
                  <Typography variant="h3" fontWeight="bold" sx={{ color: "#2196f3", mb: 0.5, fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" } }}>
                    {data.tickets.avgTimeLoss}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Avg Resolution Time (mins)
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ mt: "auto" }}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2" color="textSecondary">Total Time Loss</Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: "#ff9800" }}>{data.tickets.totalTimeLoss} min</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="textSecondary">Total Tickets</Typography>
                      <Typography variant="body2" fontWeight="bold">{data.tickets.total}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Paper>

        {/* Region Breakdown Cards */}
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 }, backgroundColor: "#FFFFFF", border: "1px solid #FFD600" }}>
          <Typography variant="h6" fontWeight="bold" mb={2} sx={{ color: "#000000", display: "flex", alignItems: "center", gap: 1 }}>
            <Business sx={{ color: "#FFD600" }} /> Region Breakdown
          </Typography>
          
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              justifyContent: isMobile ? "center" : "flex-start",
            }}
          >
            {data.regionBreakdown.map((region) => (
              <Box key={region.name} sx={{ width: regionWidth, flexShrink: 0, flexGrow: 0 }}>
                <Card sx={{ 
                  height: "100%", 
                  minHeight: 180,
                  borderTop: `4px solid #FFD600`, 
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  display: "flex",
                  flexDirection: "column",
                }}>
                  <CardContent sx={{ 
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    p: 2.5,
                  }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Typography variant="h6" fontWeight="bold" sx={{ color: "#000000" }}>
                        {region.name}
                      </Typography>
                      <Chip 
                        size="small" 
                        label={`${region.activePercentage}% Active`} 
                        sx={{ 
                          bgcolor: region.activePercentage >= 80 ? "#00e67620" : region.activePercentage >= 60 ? "#ff980020" : "#ff444420",
                          color: region.activePercentage >= 80 ? "#00e676" : region.activePercentage >= 60 ? "#ff9800" : "#ff4444",
                          fontWeight: "bold" 
                        }} 
                      />
                    </Box>
                    
                    <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={0.5} mb={1}>
                      <Chip size="small" label={`Total: ${region.total}`} sx={{ bgcolor: "#FFD60020", fontWeight: "bold" }} />
                      <Chip size="small" icon={<CheckCircle sx={{ fontSize: 12 }} />} label={`${region.active}`} sx={{ bgcolor: `${NETWORK_COLORS.active}20`, color: NETWORK_COLORS.active, fontWeight: "bold" }} />
                      <Chip size="small" icon={<Error sx={{ fontSize: 12 }} />} label={`${region.down}`} sx={{ bgcolor: `${NETWORK_COLORS.down}20`, color: NETWORK_COLORS.down, fontWeight: "bold" }} />
                      <Chip size="small" icon={<Warning sx={{ fontSize: 12 }} />} label={`${region.intermittent}`} sx={{ bgcolor: `${NETWORK_COLORS.intermittent}20`, color: NETWORK_COLORS.intermittent, fontWeight: "bold" }} />
                    </Box>
                    
                    <Box sx={{ mt: "auto" }}>
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="body2" color="textSecondary">Health</Typography>
                        <Typography variant="body2" fontWeight="bold" sx={{ color: region.activePercentage >= 80 ? "#00e676" : region.activePercentage >= 60 ? "#ff9800" : "#ff4444" }}>
                          {region.activePercentage}%
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={parseFloat(region.activePercentage)} 
                        sx={{ 
                          height: 8, 
                          borderRadius: 4, 
                          bgcolor: "#e0e0e0", 
                          "& .MuiLinearProgress-bar": { 
                            bgcolor: region.activePercentage >= 80 ? NETWORK_COLORS.active : region.activePercentage >= 60 ? NETWORK_COLORS.intermittent : NETWORK_COLORS.down 
                          } 
                        }} 
                      />
                      <Box display="flex" justifyContent="space-between" mt={0.5}>
                        <Typography variant="caption" color="textSecondary">Down: {region.downPercentage}%</Typography>
                        <Typography variant="caption" color="textSecondary">Intermittent: {region.intermittentPercentage}%</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        </Paper>

        {/* Footer */}
        <Paper sx={{ p: 2, bgcolor: "#FFD600", borderTop: "2px solid #000000" }}>
          <Box display="flex" justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} flexDirection={{ xs: "column", sm: "row" }} flexWrap="wrap" gap={1}>
            <Typography variant="body2" sx={{ color: "#000000", fontWeight: "bold" }}>
              📊 {data.summary.totalBranches} branches • {data.tickets.total} tickets across {data.regionBreakdown.length} regions
            </Typography>
            <Typography variant="body2" sx={{ color: "#000000", fontWeight: "bold" }}>
              🔄 Last fetch: {data.lastUpdated}
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default NTKDashboardSimple;